import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type PatchMailboxSchema, patchMailboxSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { MailboxForm } from "@/forms/mailbox-form";
import { client } from "@/hono";

/** Props Interface */
interface EditMailboxModalProps extends ModalProps {
	mailboxId: string;
	onComplete?: () => unknown;
}

export function EditMailboxModal({
	isOpen,
	onClose,
	mailboxId,
	onComplete,
}: EditMailboxModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.mailboxes[":mailboxId"].$get({ param: { mailboxId } });
		const data = await responseHandler(response);
		// The password never comes back; an empty field is sent as "unchanged".
		return { ...data.mailbox, password: "" } as PatchMailboxSchema;
	});
	const methods = useForm<PatchMailboxSchema & { preset?: string }>({
		defaultValues,
		resolver: resolver(patchMailboxSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: PatchMailboxSchema) {
		const response = await client.mailboxes[":mailboxId"].$patch({
			param: { mailboxId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["mailboxes"]) });
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("mailboxes.edit_mailbox")}
				isLoading={methods.formState.isLoading && !loadFailed}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				data-cy="edit-mailbox-modal"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<MailboxForm isEdit />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
